/**
 * When does an inline line-price save deserve the "why?" prompt?
 *
 * Owner ask 2026-10-29 (review amn8ne, driver AU02584: Klaas Coatings PO at
 * $215.30 keyed to $257.00): "when they adjust the price to something that's
 * different than the PO ... would you like to provide a note ... optional ...
 * that gives us a track record of why it changed."
 *
 * Pure decision so the modal can stay thin and the rule can be tested on its
 * own. The prompt is never a gate — the price is already saved when this is
 * asked — so a wrong answer here costs at most one extra or one missing ask.
 */

/** Hard cap shared with the API (OrderLineItemUpdate.price_change_reason). */
export const PRICE_REASON_MAX_LEN = 500;

export interface PriceReasonPromptInput {
  /** Line price before this save, as the row held it (string from the API). */
  previousUnitPrice: string | number | null | undefined;
  /** The price the CSR just typed (already validated numeric by the editor). */
  newUnitPrice: string | number;
  /** What the customer wrote on the PO (null for manually added lines). */
  extractedPrice: string | number | null | undefined;
}

function toNumber(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

/** Cent-level equality: "215.3" and "215.30" are the same price. */
export function samePrice(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): boolean {
  const x = toNumber(a);
  const y = toNumber(b);
  if (x == null || y == null) return false;
  return Math.round(x * 100) === Math.round(y * 100);
}

/**
 * True when the saved price now differs from the PO price AND the save
 * actually moved the price. No PO price (manual line) → never ask; price set
 * back onto the PO price → never ask; re-saving the same value → never ask.
 */
export function shouldPromptForPriceReason(input: PriceReasonPromptInput): boolean {
  const po = toNumber(input.extractedPrice);
  const next = toNumber(input.newUnitPrice);
  if (po == null || next == null) return false;
  if (samePrice(next, po)) return false;
  if (samePrice(next, input.previousUnitPrice)) return false;
  return true;
}
